import type { StickyFilterSlice, StickyFilterSliceEventBus } from '.';
import { computed, signal } from '@preact/signals-react';
import type { Signal } from '@preact/signals-react';
import { injectable } from 'src/features/ioc';
import type { StoreAdapter } from 'src/models/StoreAdapter';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';

@injectable()
export class StickyFilterSliceImpl implements StickyFilterSlice {
    #disposableStack = new DisposableStack();
    #stickyFilterValue: Signal<string> = signal('');
    #isStickyFilterSticky: Signal<boolean> = signal(false);
    #persistFilter: ((filter: string | null) => void) | null = null;
    /** The last filter value we wrote, so we can ignore the synchronous subscribe echo. */
    #lastPersistedFilter: string | null | undefined = undefined;

    eventBus: StickyFilterSliceEventBus = EventBusBuilder.create()
        .addSignal('widget::stickyFilterValue', () => computed(() => this.#stickyFilterValue.value))
        .addSignal('widget::isStickyFilterSticky', () => this.#isStickyFilterSticky)
        .addEvent('widget::updateStickyFilter', (payload) => {
            this.#stickyFilterValue.value = payload.filter;

            if (this.#persistFilter && this.#isStickyFilterSticky.value) {
                this.#lastPersistedFilter = payload.filter;
                this.#persistFilter(payload.filter);
            }
        })
        .addEvent('widget::toggleStickyFilter', () => {
            const nextIsSticky = !this.#isStickyFilterSticky.value;
            this.#isStickyFilterSticky.value = nextIsSticky;

            if (this.#persistFilter) {
                if (nextIsSticky) {
                    const valueToPersist = this.#stickyFilterValue.value || null;
                    this.#lastPersistedFilter = valueToPersist;
                    this.#persistFilter(valueToPersist);
                } else {
                    this.#lastPersistedFilter = null;
                    this.#persistFilter(null);
                }
            }
        })
        .build();

    #syncFromPersistedFilter(persistedFilter: string | null | undefined): void {
        // Skip if this is the echo from our own persist call
        if (persistedFilter === this.#lastPersistedFilter) return;
        this.#lastPersistedFilter = persistedFilter;

        this.#isStickyFilterSticky.value = Boolean(persistedFilter);
        if (persistedFilter != null) {
            this.#stickyFilterValue.value = persistedFilter;
        }
    }

    init<T extends { filter?: string | null }>(adapter: StoreAdapter<T>): void {
        this.#persistFilter = (filter) => adapter.persist({ filter } as Partial<T>);

        // Sync initial state
        const initialFilter = adapter.getState().filter;
        this.#lastPersistedFilter = initialFilter;
        this.#isStickyFilterSticky.value = Boolean(initialFilter);
        if (initialFilter != null) {
            this.#stickyFilterValue.value = initialFilter;
        }

        // Subscribe to state changes
        const unsubscribe = adapter.subscribe((state) => {
            this.#syncFromPersistedFilter(state.filter);
        });
        this.#disposableStack.defer(unsubscribe);
    }

    [Symbol.dispose]() {
        this.#disposableStack.dispose();
    }
}
